import React, { useState } from "react";
import RoboticsShell from "./RoboticsShell";
import PortraitSilhouette from "./PortraitSilhouette";
import { api } from "../../lib/api";

/**
 * Girls@TEC — core section with softer palette (`.rbx--soft`).
 *
 * Ships:
 *  - "Why We Exist" narrative (user's exact quote)
 *  - Female engineer role-model showcase (silhouette placeholders)
 *  - Scholarship application form (POST /api/robotics/newsletter with
 *    `interest: "scholarship"` payload — same collection, tagged.)
 */
export default function RoboticsGirlsAtTec() {
  const [form, setForm] = useState({ name: "", email: "", age: "", country: "", story: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.email || !form.name) {
      setStatus({ state: "err", msg: "Name and email are required." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/robotics/newsletter", {
        email: form.email,
        interest: "girls_at_tec_scholarship",
        meta: form,
      });
      setStatus({ state: "ok", msg: "Application received. You'll hear from us within a week." });
      setForm({ name: "", email: "", age: "", country: "", story: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again later.";
      setStatus({ state: "err", msg: String(detail) });
    }
  }

  return (
    <RoboticsShell soft>
      <section className="rbx-page" data-testid="rbx-page-girls">
        {/* Warm-palette hero — nano-banana rendered, opaque JPG with WebP sibling. */}
        <figure
          className="rbx-girls-hero"
          data-testid="rbx-girls-hero"
          style={{
            position: "relative",
            margin: "0 0 32px",
            borderRadius: 20,
            overflow: "hidden",
            aspectRatio: "3 / 2",
            background: "var(--rbx-cream, #f5ecdf)",
            boxShadow: "0 18px 50px -22px rgba(60, 24, 8, 0.35)",
          }}
        >
          <picture>
            <source srcSet="/robotics/brand/hero_girls_workbench.webp" type="image/webp" />
            <img
              src="/robotics/brand/hero_girls_workbench.jpg"
              alt="Two young girls at a sunlit workbench, one soldering carefully, the other holding a small wheeled robot they have just built."
              loading="eager"
              decoding="async"
              data-testid="rbx-girls-hero-img"
              style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
            />
          </picture>
          <figcaption
            data-testid="rbx-girls-hero-headline"
            style={{
              position: "absolute",
              left: 0, right: 0, bottom: 0,
              padding: "clamp(18px, 4vw, 40px)",
              background: "linear-gradient(180deg, rgba(38, 18, 6, 0) 0%, rgba(38, 18, 6, 0.55) 60%, rgba(38, 18, 6, 0.78) 100%)",
              color: "#fff8ee",
              fontFamily: "var(--rbx-font-serif, Georgia, 'Times New Roman', serif)",
              fontSize: "clamp(24px, 3.4vw, 44px)",
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
              textShadow: "0 1px 4px rgba(0,0,0,0.35)",
            }}
          >
            There&rsquo;s a genius inside her too.
          </figcaption>
        </figure>

        <span className="rbx-eyebrow" style={{ color: "var(--rbx-coral)" }}>Section · Girls@TEC</span>
        <h1 className="rbx-page__title" data-testid="rbx-girls-title">
          The girls who&rsquo;ll build tomorrow&rsquo;s engines.
        </h1>
        <p className="rbx-page__lede">
          Core section. Not an add-on. Not a token page. A structural
          commitment.
        </p>

        <div className="rbx-heritage" style={{ padding: 0, margin: "8px 0 40px" }}>
          <div className="rbx-heritage__inner" style={{ borderLeftColor: "var(--rbx-coral)" }} data-testid="rbx-girls-why">
            <strong>Why we exist.</strong> Girls are naturally strong in
            mathematics but drop out due to social stigma. In Asia especially,
            they are isolated by social pressure — but really they want to join
            the new world of AI and tech. Girls@TEC exists to keep them in the
            game.
          </div>
        </div>

        <h2 className="rbx-page__title" style={{ fontSize: 24, marginTop: 8, marginBottom: 4 }}>
          Role models — the ones we point to.
        </h2>
        <p style={{ color: "var(--rbx-ink-mute)", maxWidth: "62ch" }}>
          A Phase 3 storybook drama for each. For now, a name and a role.
        </p>
        <div className="rbx-heroes-grid" data-testid="rbx-girls-role-models">
          {ROLE_MODELS.map((m) => (
            <article key={m.slug} className="rbx-card rbx-hero-tile" data-testid={`rbx-girls-model-${m.slug}`}>
              <div className="rbx-hero-tile__portrait">
                <PortraitSilhouette tone="warm" />
              </div>
              <div className="rbx-hero-tile__body">
                <div className="rbx-hero-tile__name">{m.name}</div>
                <div className="rbx-hero-tile__role">{m.role}</div>
                <span className="rbx-chip-q4">Story in Phase 3</span>
              </div>
            </article>
          ))}
        </div>

        <h2 className="rbx-page__title" style={{ fontSize: 24, marginTop: 56, marginBottom: 6 }}>
          Scholarship application
        </h2>
        <p style={{ color: "var(--rbx-ink-mute)", maxWidth: "62ch", marginBottom: 12 }}>
          Applications reviewed manually. Priority for South Asia, sub-Saharan
          Africa, and rural areas. Preference for kids whose family income is a
          barrier. Tell us what draws you to robotics — in your own voice.
        </p>
        <form className="rbx-form" onSubmit={onSubmit} data-testid="rbx-girls-form" style={{ maxWidth: 620 }}>
          <div className="rbx-form__row">
            <div>
              <label className="rbx-label" htmlFor="rbx-girls-name">Full name</label>
              <input id="rbx-girls-name" className="rbx-input" value={form.name}
                     onChange={(e) => update("name", e.target.value)} data-testid="rbx-girls-name"/>
            </div>
            <div>
              <label className="rbx-label" htmlFor="rbx-girls-email">Email</label>
              <input id="rbx-girls-email" className="rbx-input" type="email" value={form.email}
                     onChange={(e) => update("email", e.target.value)} data-testid="rbx-girls-email"/>
            </div>
          </div>
          <div className="rbx-form__row">
            <div>
              <label className="rbx-label" htmlFor="rbx-girls-age">Age</label>
              <input id="rbx-girls-age" className="rbx-input" type="number" min="8" max="17" value={form.age}
                     onChange={(e) => update("age", e.target.value)} data-testid="rbx-girls-age"/>
            </div>
            <div>
              <label className="rbx-label" htmlFor="rbx-girls-country">Country</label>
              <input id="rbx-girls-country" className="rbx-input" value={form.country}
                     onChange={(e) => update("country", e.target.value)} data-testid="rbx-girls-country"/>
            </div>
          </div>
          <div>
            <label className="rbx-label" htmlFor="rbx-girls-story">Why do you want to build robots?</label>
            <textarea id="rbx-girls-story" className="rbx-textarea" value={form.story}
                      onChange={(e) => update("story", e.target.value)} data-testid="rbx-girls-story"
                      placeholder="One paragraph is plenty."/>
          </div>
          <div>
            <button type="submit" className="rbx-btn rbx-btn--primary"
                    disabled={status.state === "loading"} data-testid="rbx-girls-submit">
              {status.state === "loading" ? "Sending…" : "Apply"}
            </button>
          </div>
          {status.msg ? (
            <p className={`rbx-form__msg ${status.state === "ok" ? "rbx-form__msg--ok" : "rbx-form__msg--err"}`}
               data-testid="rbx-girls-msg">
              {status.msg}
            </p>
          ) : null}
        </form>
      </section>
    </RoboticsShell>
  );
}

const ROLE_MODELS = [
  { slug: "ada_lovelace",       name: "Ada Lovelace",       role: "First programmer, 1843" },
  { slug: "grace_hopper",       name: "Grace Hopper",       role: "COBOL · first debug" },
  { slug: "katherine_johnson",  name: "Katherine Johnson",  role: "NASA orbital mechanics" },
  { slug: "sophie_germain",     name: "Sophie Germain",     role: "Elasticity theory, 1816" },
  { slug: "marie_curie",        name: "Marie Curie",        role: "Radioactivity" },
  { slug: "hedy_lamarr",        name: "Hedy Lamarr",        role: "Frequency-hopping radio" },
  { slug: "timnit_gebru",       name: "Timnit Gebru",       role: "AI ethics" },
  { slug: "fei_fei_li",         name: "Fei-Fei Li",         role: "ImageNet · vision" },
];
